import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';
import { authStore } from './authStore';

// Get the API base URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_IOT || '';

// Types
export interface ChartDataPoint {
  timestamp: string;
  nrg?: number;
  consumed_nrg?: number;
  csq?: number;
  r1?: number;
  r2?: number;
  r3?: number;
  r1f?: number;
  r2f?: number;
  r3f?: number;
  [key: string]: any; // For any additional fields
}

export interface ChartData {
  data: ChartDataPoint[];
  metadata: {
    count: number;
    sampling: string;
    date_from: string;
    date_to: string;
    device_ids: string[];
  };
}

export type Sampling = '15min' | '1h' | '1d';

class ChartStore {
  chartData: ChartData | null = null;
  loading = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Get authenticated API headers
  get headers() {
    return {
      headers: {
        Authorization: `Bearer ${authStore.accessToken}`
      }
    };
  }

  // Fetch energy chart data
  async fetchEnergyChartData(
    dateFrom: string, 
    dateTo: string, 
    deviceIds: string | string[],
    sampling: Sampling = '1h'
  ) {
    this.loading = true;
    this.error = null;

    try {
      // Format device IDs as comma-separated string if it's an array
      const deviceIdParam = Array.isArray(deviceIds) ? deviceIds.join(',') : deviceIds;
      
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/energy-chart`, {
          ...this.headers,
          params: {
            date_from: dateFrom,
            date_to: dateTo,
            device_id: deviceIdParam,
            sampling
          }
        }
      );
      
      runInAction(() => {
        if (response.data && Array.isArray(response.data.data)) {
          this.chartData = response.data;
        } else {
          console.error('Invalid chart data format:', response.data);
          this.error = 'Invalid data format received from server';
          this.chartData = null;
        }
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to load chart data';
        this.loading = false;
        
        if (error.response?.status === 401) {
          authStore.logout();
        }
      });
    }
  }

  // Get device IDs from chart data
  get deviceIds(): string[] {
    return this.chartData?.metadata.device_ids || [];
  }

  // Reset chart data state
  reset() {
    this.chartData = null;
    this.loading = false;
    this.error = null;
  }
}

export const chartStore = new ChartStore(); 